import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isStripeTestMode } from "@/config/stripePlans";
import { Button } from "@/components/ui/button";
import AdminTabActionBar from "@/components/admin_dashboard/AdminTabActionBar";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, MoreHorizontal, TableIcon, CalendarDays, ChevronLeft, ChevronRight, Search, SlidersHorizontal, X, Lock, RefreshCw, Upload, Download } from "lucide-react";
import BookingsImportDialog from "@/components/admin_dashboard/BookingsImportDialog";
import BookingsExportDialog from "@/components/admin_dashboard/BookingsExportDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  getDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  isSameDay, isSameMonth, isToday as isDateToday, isThisWeek, isThisMonth,
  subDays as dateFnsSubDays, isAfter,
} from "date-fns";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import BookingDetailsModal from "@/components/modules/modals/BookingDetailsModal";
import { Eye, Pencil } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface Booking {
  booking_id: string;
  short_id: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  booking_date: string;
  booking_time: string;
  status: string;
  notes: string | null;
  created_at: string;
  listing_id: string | null;
  total_paid: number | null;
  payment_method: string | null;
  listings?: {
    name: string;
    price: number;
    business_id: string;
  } | null;
}

type ViewMode = "table" | "calendar";
type CalendarScale = "day" | "week" | "month";

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7am–8pm

type DateRangeFilter = "all" | "today" | "this_week" | "this_month" | "last_30";

interface BookingFilters {
  statuses: string[];
  dateRange: DateRangeFilter;
  listings: string[];
  priceMin: string;
  priceMax: string;
}

const DEFAULT_BOOKING_FILTERS: BookingFilters = {
  statuses: [],
  dateRange: "all",
  listings: [],
  priceMin: "",
  priceMax: "",
};

const DATE_RANGE_OPTIONS: { value: DateRangeFilter; label: string }[] = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "last_30", label: "Last 30 days" },
];

// Single-line cell text that truncates and reveals the full value in a tooltip on hover.
// `tip` overrides the tooltip content so multi-line cells can show everything at once.
function CellText({ children, className, tip, lines }: { children: React.ReactNode; className?: string; tip?: React.ReactNode; lines?: React.ReactNode[] }) {
  if (children == null || children === "") return <span className={className}>—</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {lines ? (
          <div className="min-w-0">{lines}</div>
        ) : (
          <span className={cn("block truncate", className)}>{children}</span>
        )}
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[320px] whitespace-pre-wrap break-words text-xs">{tip ?? children}</TooltipContent>
    </Tooltip>
  );
}

const bookingDateLabel = (booking: { booking_date?: string | null }) =>
  booking.booking_date ? format(new Date(booking.booking_date + "T00:00:00"), "d MMM yyyy") : "—";

const GeneralBookings = () => {
  const { businessId } = useAuth();
  const { activeOutlet, isAllOutlets } = useOutlet();
  const queryClient = useQueryClient();
  const [view, setView] = useState<ViewMode>("table");
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarScale, setCalendarScale] = useState<CalendarScale>("month");
  const [selectedBookingForDetails, setSelectedBookingForDetails] = useState<Booking | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<BookingFilters>(DEFAULT_BOOKING_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  // Status change confirmation dialog state
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    bookingId: string;
    currentStatus: string;
    newStatus: string;
  } | null>(null);

  const activeOutletId = activeOutlet?.outlet_id || null;

  const { data: bookings = [], isLoading: queryLoading, refetch, isRefetching } = useQuery({
    queryKey: ['bookings', businessId, activeOutletId],
    queryFn: async () => {
      if (!businessId) return [];

      let query = supabase
        .from("bookings")
        .select(`
          *,
          listings!inner (name, price, business_id, outlet_id)
        `)
        .eq("listings.business_id", businessId);

      // Filter by outlet
      if (activeOutletId) {
        query = query.eq("listings.outlet_id", activeOutletId);
      }

      const { data, error } = await query
        .order("created_at", { ascending: false });

      if (error) {
        toast.error("Failed to fetch bookings");
        throw error;
      }
      return (data as any[]) || [];
    },
    enabled: !!businessId,
    staleTime: 30000,
  });

  const requestStatusChange = (bookingId: string, currentStatus: string, newStatus: string) => {
    if (currentStatus === newStatus) return;
    setPendingStatusChange({ bookingId, currentStatus, newStatus });
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    const { bookingId, newStatus } = pendingStatusChange;

    let error: unknown = null;
    if (newStatus === "cancelled") {
      // Route cancels through refund-booking. The single chokepoint that flips
      // status → cancelled AND emits booking/cancelled (→ waitlist auto-fill).
      // It also handles any Stripe refund / pay-at-venue (no-refund) correctly.
      const { error: fnError } = await supabase.functions.invoke("refund-booking", {
        body: {
          bookingId,
          scope: "one",
          // Mirrors the public cancel page: forward the build-time Stripe mode so
          // refund-booking picks the matching key (test on localhost, live in prod).
          test_mode: isStripeTestMode,
          // Business override: owners can cancel inside the 24h window.
          bypass_24h: true,
          cancelNotes: "Cancelled by the business from the bookings dashboard.",
        },
      });
      error = fnError;
    } else {
      const { error: updError } = await supabase
        .from("bookings")
        .update({ status: newStatus })
        .eq("booking_id", bookingId);
      error = updError;

      // Attendance outcomes fan out through mark-attendance: it emits the
      // customer-graph event (NoShowDetected / BookingCompleted) and the
      // booking/no-show or booking/completed workflow trigger (rebook recovery
      // / post-session follow-up). Best-effort. The status flip already
      // succeeded above, so a hiccup here must not surface as a failure.
      if (!updError && (newStatus === "no_show" || newStatus === "completed")) {
        try {
          await supabase.functions.invoke("mark-attendance", {
            body: { bookingId, status: newStatus },
          });
        } catch (e) {
          console.error("mark-attendance invoke failed:", e);
        }
      }
    }

    if (error) {
      toast.error("Failed to update status");
    } else {
      toast.success("Status updated");
      // Force an immediate refetch rather than a passive invalidate: the
      // side-effect fns above (refund-booking / mark-attendance) can settle a
      // beat after the row write, and a plain invalidate occasionally left the
      // row showing its old status until a manual reload (QA F-A5-3).
      await queryClient.refetchQueries({ queryKey: ["bookings", businessId, activeOutletId] });
    }
    setPendingStatusChange(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed": return "bg-green-500";
      case "cancelled": return "bg-destructive";
      case "completed": return "bg-blue-500";
      case "no_show": return "bg-amber-500";
      default: return "bg-muted-foreground/50";
    }
  };

  // "no_show" → "No-show" (the rest just capitalize cleanly via CSS).
  const formatStatus = (status: string) => (status === "no_show" ? "No-show" : status);

  const getBookingsForDate = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return bookings.filter((b) => b.booking_date === dateStr);
  };

  // Navigation
  const navigateBack = () => {
    if (calendarScale === "month") setCalendarDate(subMonths(calendarDate, 1));
    else if (calendarScale === "week") setCalendarDate(subWeeks(calendarDate, 1));
    else setCalendarDate(subDays(calendarDate, 1));
  };

  const navigateForward = () => {
    if (calendarScale === "month") setCalendarDate(addMonths(calendarDate, 1));
    else if (calendarScale === "week") setCalendarDate(addWeeks(calendarDate, 1));
    else setCalendarDate(addDays(calendarDate, 1));
  };

  const getNavigationLabel = () => {
    if (calendarScale === "month") return format(calendarDate, "MMMM yyyy");
    if (calendarScale === "week") {
      const weekStart = startOfWeek(calendarDate);
      const weekEnd = endOfWeek(calendarDate);
      return `${format(weekStart, "d MMM")} — ${format(weekEnd, "d MMM yyyy")}`;
    }
    return format(calendarDate, "EEEE, d MMMM yyyy");
  };

  // Derive unique listing names for filter options
  const uniqueListings = useMemo(() => {
    const names = new Set<string>();
    bookings.forEach((b) => { if (b.listings?.name) names.add(b.listings.name); });
    return Array.from(names).sort();
  }, [bookings]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.statuses.length > 0) count++;
    if (filters.dateRange !== "all") count++;
    if (filters.listings.length > 0) count++;
    if (filters.priceMin || filters.priceMax) count++;
    return count;
  }, [filters]);

  // Apply search + filters
  const filteredBookings = useMemo(() => {
    let result = bookings;

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((b) =>
        (b.short_id?.toLowerCase().includes(q)) ||
        b.booking_id.toLowerCase().includes(q) ||
        b.customer_name.toLowerCase().includes(q) ||
        b.customer_email.toLowerCase().includes(q) ||
        (b.listings?.name?.toLowerCase().includes(q))
      );
    }

    // Status filter
    if (filters.statuses.length > 0) {
      result = result.filter((b) => filters.statuses.includes(b.status));
    }

    // Date range filter
    if (filters.dateRange !== "all") {
      const now = new Date();
      result = result.filter((b) => {
        if (!b.booking_date) return false;
        const bookingDate = new Date(b.booking_date + "T00:00:00");
        if (isNaN(bookingDate.getTime())) return false; // Invalid Date
        switch (filters.dateRange) {
          case "today":
            return isDateToday(bookingDate);
          case "this_week":
            return isThisWeek(bookingDate);
          case "this_month":
            return isThisMonth(bookingDate);
          case "last_30":
            return isAfter(bookingDate, dateFnsSubDays(now, 30));
          default:
            return true;
        }
      });
    }

    // Listing filter
    if (filters.listings.length > 0) {
      result = result.filter((b) => b.listings?.name && filters.listings.includes(b.listings.name));
    }

    // Price range filter
    if (filters.priceMin || filters.priceMax) {
      const min = filters.priceMin ? parseFloat(filters.priceMin) : 0;
      const max = filters.priceMax ? parseFloat(filters.priceMax) : Infinity;
      result = result.filter((b) => {
        const price = Number(b.total_paid ?? b.listings?.price ?? 0);
        return price >= min && price <= max;
      });
    }

    return result;
  }, [bookings, searchQuery, filters]);

  const clearFilters = () => setFilters(DEFAULT_BOOKING_FILTERS);

  const loading = queryLoading && !!businessId;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  // Booking event pill component
  const BookingPill = ({ booking, compact = false }: { booking: Booking; compact?: boolean }) => (
    <div
      className={cn(
        "flex items-start gap-1 px-1.5 py-0.5 rounded bg-muted/60",
        compact ? "text-[10px]" : "text-xs"
      )}
      title={`${booking.booking_time} — ${booking.listings?.name || "No listing"} — ${booking.customer_name}`}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1", getStatusColor(booking.status))} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <span className="font-medium">{booking.booking_time}</span>
          {booking.listings?.name && (
            <span className="text-muted-foreground truncate">· {booking.listings.name}</span>
          )}
        </div>
        <div className="text-muted-foreground truncate">{booking.customer_name}</div>
      </div>
    </div>
  );

  // Month view
  const renderMonthView = () => {
    const monthStart2 = startOfMonth(calendarDate);
    const monthEnd2 = endOfMonth(calendarDate);
    const days = eachDayOfInterval({ start: monthStart2, end: monthEnd2 });
    const startPad = getDay(monthStart2);

    return (
      <>
        <div className="grid grid-cols-7 mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-center text-xs font-medium text-muted-foreground py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 border-t border-l">
          {Array.from({ length: startPad }).map((_, i) => (
            <div key={`e-${i}`} className="border-r border-b min-h-[100px] bg-muted/30" />
          ))}
          {days.map((day) => {
            const dayBookings = getBookingsForDate(day);
            const isToday = isSameDay(day, new Date());
            return (
              <div key={day.toISOString()} className={cn("border-r border-b min-h-[100px] p-1.5", isToday && "bg-primary/5")}>
                <div className={cn("text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full", isToday && "bg-primary text-primary-foreground")}>
                  {format(day, "d")}
                </div>
                <div className="space-y-0.5">
                  {dayBookings.slice(0, 2).map((b) => <BookingPill key={b.booking_id} booking={b} compact />)}
                  {dayBookings.length > 2 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setCalendarDate(day); setCalendarScale("day"); }}
                      className="text-[10px] text-primary hover:underline pl-1.5 pt-0.5 text-left w-full"
                    >
                      View this day (+{dayBookings.length - 2} more)
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // Week view
  const renderWeekView = () => {
    const weekStart2 = startOfWeek(calendarDate);
    const days = eachDayOfInterval({ start: weekStart2, end: endOfWeek(calendarDate) });

    return (
      <div className="grid grid-cols-7 border-t border-l">
        {days.map((day) => {
          const dayBookings = getBookingsForDate(day);
          const isToday = isSameDay(day, new Date());
          return (
            <div key={day.toISOString()} className={cn("border-r border-b min-h-[300px] p-2", isToday && "bg-primary/5")}>
              <div className="text-center mb-2">
                <div className="text-[10px] text-muted-foreground uppercase">{format(day, "EEE")}</div>
                <div className={cn("text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full mx-auto", isToday && "bg-primary text-primary-foreground")}>
                  {format(day, "d")}
                </div>
              </div>
              <div className="space-y-1">
                {dayBookings.slice(0, 2).map((b) => <BookingPill key={b.booking_id} booking={b} />)}
                {dayBookings.length > 2 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setCalendarDate(day); setCalendarScale("day"); }}
                    className="text-xs text-primary hover:underline text-center w-full pt-1"
                  >
                    View this day (+{dayBookings.length - 2} more)
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Day view
  const renderDayView = () => {
    const dayBookings = getBookingsForDate(calendarDate);

    return (
      <div className="space-y-1">
        {dayBookings.length === 0 ? (
          <p className="text-muted-foreground text-center py-12 text-sm">No bookings on this day.</p>
        ) : (
          dayBookings.map((b) => (
            <div key={b.booking_id} className="flex items-start gap-3 border rounded-lg p-3">
              <span className={cn("w-2 h-2 rounded-full flex-shrink-0 mt-1.5", getStatusColor(b.status))} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{b.booking_time}</span>
                  {b.listings?.name && (
                    <span className="text-sm text-muted-foreground">· {b.listings.name}</span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">{b.customer_name}</div>
                <div className="text-xs text-muted-foreground">{b.customer_email}</div>
              </div>
              <Badge variant="secondary" className="text-[10px] capitalize">{b.status}</Badge>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <AdminTabActionBar className="gap-2">
        {view === "table" && bookings.length > 0 && (
          <>
              <div className="relative w-full max-w-[300px]">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search bookings..."
                  className="admin-toolbar-input pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="admin-toolbar-button shrink-0 gap-1.5">
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    Filter
                    {activeFilterCount > 0 && (
                      <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">Filters</h4>
                      {activeFilterCount > 0 && (
                        <button onClick={clearFilters} className="text-xs text-primary hover:underline">
                          Clear all
                        </button>
                      )}
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Status</p>
                      <div className="flex flex-wrap gap-2">
                        {["confirmed", "completed", "no_show", "cancelled"].map((status) => (
                          <label key={status} className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox
                              checked={filters.statuses.includes(status)}
                              onCheckedChange={(checked) => {
                                setFilters((prev) => ({
                                  ...prev,
                                  statuses: checked
                                    ? [...prev.statuses, status]
                                    : prev.statuses.filter((s) => s !== status),
                                }));
                              }}
                            />
                            <span className="text-sm capitalize">{formatStatus(status)}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Date range</p>
                      <div className="flex flex-wrap gap-1.5">
                        {DATE_RANGE_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => setFilters((prev) => ({ ...prev, dateRange: opt.value }))}
                            className={cn(
                              "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                              filters.dateRange === opt.value
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                            )}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Listing */}
                    {uniqueListings.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Listing</p>
                        <div className="space-y-1.5 max-h-[120px] overflow-y-auto">
                          {uniqueListings.map((name) => (
                            <label key={name} className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox
                                checked={filters.listings.includes(name)}
                                onCheckedChange={(checked) => {
                                  setFilters((prev) => ({
                                    ...prev,
                                    listings: checked
                                      ? [...prev.listings, name]
                                      : prev.listings.filter((l) => l !== name),
                                  }));
                                }}
                              />
                              <span className="text-sm">{name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Price Range */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Price range</p>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          placeholder="Min"
                          className="h-8 text-sm"
                          value={filters.priceMin}
                          onChange={(e) => setFilters((prev) => ({ ...prev, priceMin: e.target.value }))}
                        />
                        <span className="text-muted-foreground text-xs">—</span>
                        <Input
                          type="number"
                          placeholder="Max"
                          className="h-8 text-sm"
                          value={filters.priceMax}
                          onChange={(e) => setFilters((prev) => ({ ...prev, priceMax: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="admin-toolbar-button gap-1.5"
            onClick={() => setImportOpen(true)}
            disabled={isAllOutlets || !businessId}
            title={isAllOutlets ? "Select an outlet to import bookings" : undefined}
          >
            <Upload className="h-3.5 w-3.5" />
            Import
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="admin-toolbar-button gap-1.5"
            onClick={() => setExportOpen(true)}
            disabled={!businessId || bookings.length === 0}
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="admin-toolbar-button w-7 px-0"
            aria-label="Refresh bookings"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`} />
          </Button>
          <div className="admin-toolbar-segmented">
            <Button
              variant="ghost"
              size="sm"
              className={cn("h-7 gap-1.5 rounded-none px-2.5", view === "table" && "bg-muted")}
              onClick={() => setView("table")}
            >
              <TableIcon className="h-3.5 w-3.5" />
              Table
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn("h-7 gap-1.5 rounded-none px-2.5", view === "calendar" && "bg-muted")}
              onClick={() => setView("calendar")}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Calendar
            </Button>
          </div>
        </div>
      </AdminTabActionBar>
        {bookings.length === 0 && view === "table" ? (
          <p className="text-muted-foreground text-center py-12">Bookings from your storefront will appear here.</p>
        ) : view === "table" ? (
          <div className="space-y-4">

            <div className="admin-data-table-shell">
            <div className="admin-data-table-scroll w-full overflow-x-auto bg-card">
            <Table className="admin-data-table" style={{ minWidth: 1368 }}>
              <TableHeader className="admin-data-table-header">
                <TableRow className="admin-data-table-header-row">
                  <TableHead style={{ width: 150 }}>Status</TableHead>
                  <TableHead style={{ width: 150 }}>Order ID</TableHead>
                  <TableHead style={{ width: 130 }}>Date & time</TableHead>
                  <TableHead style={{ width: 220 }}>Listing</TableHead>
                  <TableHead style={{ width: 240 }}>Customer</TableHead>
                  <TableHead style={{ width: 90 }}>Price</TableHead>
                  <TableHead style={{ width: 120 }}>Payment</TableHead>
                  <TableHead style={{ width: 220 }}>Notes</TableHead>
                  <TableHead style={{ width: 48 }}></TableHead>
                </TableRow>
              </TableHeader>
              <TooltipProvider delayDuration={200}><TableBody className="admin-data-table-body">
                {filteredBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                      No bookings found matching your search or filters.
                    </TableCell>
                  </TableRow>
                ) : filteredBookings.map((booking) => (
                  <TableRow key={booking.booking_id} className="admin-data-table-row">
                    <TableCell className="admin-data-table-cell">
                      {booking.status === "cancelled" ? (
                        <div className="flex items-center gap-2 w-[125px] h-8 px-3 rounded-md border bg-muted/50 cursor-not-allowed">
                          <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                          <span className="text-xs font-medium capitalize text-muted-foreground">Cancelled</span>
                          <Lock className="w-3 h-3 text-muted-foreground/60 ml-auto" />
                        </div>
                      ) : (
                        <Select
                          value={booking.status}
                          disabled={isAllOutlets}
                          onValueChange={(value) => requestStatusChange(booking.booking_id, booking.status, value)}
                        >
                          <SelectTrigger className="w-[125px] h-8 text-xs font-medium focus:ring-1 focus:ring-primary/20">
                            <SelectValue>
                              <div className="flex items-center gap-2">
                                <div className={cn(
                                  "w-2 h-2 rounded-full shrink-0",
                                  booking.status === "confirmed" && "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]",
                                  booking.status === "completed" && "bg-muted-foreground/40",
                                  booking.status === "no_show" && "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]",
                                  booking.status === "cancelled" && "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]"
                                )} />
                                <span className="capitalize">{formatStatus(booking.status)}</span>
                              </div>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="confirmed">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                                <span>Confirmed</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="completed">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-muted-foreground/40 shrink-0" />
                                <span>Completed</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="no_show">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                                <span>No-show</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="cancelled">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                                <span>Cancelled</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="admin-data-table-cell text-xs text-muted-foreground font-mono"><CellText>{booking.short_id || booking.booking_id?.slice(0, 8)}</CellText></TableCell>
                    <TableCell className="admin-data-table-cell">
                      <CellText
                        tip={[bookingDateLabel(booking), booking.booking_time].filter(Boolean).join(", ")}
                        lines={[
                          <span key="d" className="block truncate text-sm font-medium">{bookingDateLabel(booking)}</span>,
                          <span key="t" className="block truncate text-xs text-muted-foreground">{booking.booking_time}</span>,
                        ]}
                      >
                        {bookingDateLabel(booking)}
                      </CellText>
                    </TableCell>
                    <TableCell className="admin-data-table-cell"><CellText>{booking.listings?.name}</CellText></TableCell>
                    <TableCell className="admin-data-table-cell">
                      <CellText
                        tip={[booking.customer_name, booking.customer_email].filter(Boolean).join("\n")}
                        lines={[
                          <span key="n" className="block truncate font-medium">{booking.customer_name}</span>,
                          <span key="e" className="block truncate text-xs text-muted-foreground">{booking.customer_email}</span>,
                        ]}
                      >
                        {booking.customer_name}
                      </CellText>
                    </TableCell>
                    <TableCell className="admin-data-table-cell font-medium">
                      {(booking.total_paid != null ? `$${Number(booking.total_paid).toFixed(2)}` : booking.listings?.price != null ? `$${Number(booking.listings.price).toFixed(2)}` : "—")}
                    </TableCell>
                    <TableCell className="admin-data-table-cell">
                      {booking.payment_method === "stripe" ? (
                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-50">Stripe</Badge>
                      ) : booking.payment_method === "manual" ? (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-50">Pay at venue</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground capitalize">{booking.payment_method || "—"}</span>
                      )}
                    </TableCell>
                    <TableCell className="admin-data-table-cell">
                      <CellText className="text-sm text-muted-foreground" tip={booking.notes ? <><span className="mb-1 block font-semibold text-muted-foreground">Customer note</span>{booking.notes}</> : undefined}>
                        {booking.notes}
                      </CellText>
                    </TableCell>
                    <TableCell className="admin-data-table-cell">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedBookingForDetails(booking);
                              setIsViewMode(true);
                              setIsDetailsModalOpen(true);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          {!isAllOutlets && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedBookingForDetails(booking);
                                setIsViewMode(false);
                                setIsDetailsModalOpen(true);
                              }}
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody></TooltipProvider>
            </Table>
            </div>
            </div>
          </div>
        ) : (
          <div>
            {/* Calendar controls */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={navigateBack}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={navigateForward}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <h3 className="text-sm font-medium ml-2">{getNavigationLabel()}</h3>
                <Button variant="outline" size="sm" className="h-6 px-2.5 text-xs ml-2" onClick={() => setCalendarDate(new Date())}>
                  Skip to today
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 border rounded-lg p-1">
                  {(["day", "week", "month"] as CalendarScale[]).map((scale) => (
                    <Button
                      key={scale}
                      variant={calendarScale === scale ? "secondary" : "ghost"}
                      size="sm"
                      className="h-6 px-2.5 text-xs capitalize"
                      onClick={() => setCalendarScale(scale)}
                    >
                      {scale}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {calendarScale === "month" && renderMonthView()}
            {calendarScale === "week" && renderWeekView()}
            {calendarScale === "day" && renderDayView()}
          </div>
        )}
        <div className="flex justify-end mt-4">
          <p className="text-[12px] text-muted-foreground tracking-wider font-medium">
            Showing {filteredBookings.length} of booking(s)
          </p>
        </div>

      {businessId && (
        <BookingsImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          businessId={businessId}
          onImported={() =>
            queryClient.invalidateQueries({
              queryKey: ["bookings", businessId, activeOutletId],
            })
          }
        />
      )}

      {businessId && (
        <BookingsExportDialog
          open={exportOpen}
          onOpenChange={setExportOpen}
          businessId={businessId}
          businessName={activeOutlet?.outlet_name || "bookings"}
          allBookings={bookings as never[]}
          filteredBookings={filteredBookings as never[]}
        />
      )}

      <BookingDetailsModal
        booking={selectedBookingForDetails}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedBookingForDetails(null);
          setIsViewMode(false);
        }}
        onSaveSuccess={() => queryClient.invalidateQueries({ queryKey: ["bookings", businessId, activeOutletId] })}
        businessId={businessId}
        readOnly={isViewMode}
      />

      {/* Status change confirmation dialog */}
      <AlertDialog open={!!pendingStatusChange} onOpenChange={(open) => !open && setPendingStatusChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change booking status?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span>Are you sure you want to change the status from{" "}
                <span className="font-semibold capitalize">{formatStatus(pendingStatusChange?.currentStatus ?? "")}</span>{" "}
                to{" "}
                <span className="font-semibold capitalize">{formatStatus(pendingStatusChange?.newStatus ?? "")}</span>?
              </span>
              {pendingStatusChange?.newStatus === "cancelled" && (
                <span className="block text-destructive font-medium mt-1">
                  Warning: Once a booking is cancelled, the status cannot be changed again.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmStatusChange}
              className={pendingStatusChange?.newStatus === "cancelled" ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GeneralBookings;
